/**
 * Tracks which surfaces have asked for app-level hotkeys to be suppressed.
 *
 * A module-level store rather than redux state: `UIProvider` (and therefore the
 * alert/confirm dialogs it renders) mounts outside the redux `Provider`, so
 * those components can't dispatch. Read it with `useAreGlobalHotkeysEnabled`.
 *
 * Suppressors are keyed by token so they compose — hotkeys stay disabled until
 * every suppressor has released, and releasing twice is a no-op.
 */
const suppressors = new Set<string>();
const listeners = new Set<VoidFunction>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

export function suppressGlobalHotkeys(token: string): void {
  if (suppressors.has(token)) {
    return;
  }
  suppressors.add(token);
  emit();
}

export function releaseGlobalHotkeys(token: string): void {
  if (!suppressors.delete(token)) {
    return;
  }
  emit();
}

export function getAreGlobalHotkeysEnabled(): boolean {
  return suppressors.size === 0;
}

export function subscribeToGlobalHotkeys(listener: VoidFunction): VoidFunction {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
